/**
 * Song Selector Component
 * 
 * A modal component for searching and selecting songs to add to a service.
 * Features include:
 * - Real-time song search functionality
 * - Display of song details including title, key, and streaming links
 * - Key selection for each song
 * - Loading states and error handling for API interactions
 * - Integration with the church's song database
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Autocomplete,
  InputAdornment,
  Chip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SpotifyIcon from '@mui/icons-material/Audiotrack';
import { API_ENDPOINTS } from '../../config/api';

const MUSICAL_KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const SongSelector = ({ open, onClose, onSelect, customFetch }) => {
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSong, setSelectedSong] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchSongs();
    }
  }, [open]);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customFetch.get(API_ENDPOINTS.SONGS.BASE);
      setSongs(response.data);
    } catch (error) {
      console.error('Error fetching songs:', error);
      setError('Failed to fetch songs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (value.length > 0) {
      try {
        setLoading(true);
        setError(null);
        const response = await customFetch.get(API_ENDPOINTS.SONGS.BASE, {
          params: { search: value }
        });
        setSongs(response.data);
      } catch (error) {
        console.error('Error searching songs:', error);
        setError('Failed to search songs. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      fetchSongs();
    }
  };

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    setSelectedKey(song.defaultKey);
  };

  const handleConfirm = () => {
    if (selectedSong && selectedKey) {
      onSelect({
        song: selectedSong,
        key: selectedKey,
        defaultKey: selectedSong.defaultKey
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSong(null);
    setSelectedKey('');
    setSearchTerm('');
    onClose();
  };

  const renderStreamingLinks = (song) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {song.youtubeUrl && (
        <IconButton
          size="small"
          href={song.youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'error.main' }}
        >
          <YouTubeIcon fontSize="small" />
        </IconButton>
      )}
      {song.spotifyUrl && (
        <IconButton
          size="small"
          href={song.spotifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'success.main' }}
        >
          <SpotifyIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select Song</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search songs..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
          {songs.map((song) => (
            <ListItem
              key={song._id}
              button
              selected={selectedSong?._id === song._id}
              onClick={() => handleSongSelect(song)}
            >
              <ListItemText
                primary={song.title}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={<MusicNoteIcon />}
                      label={song.defaultKey}
                      size="small"
                      variant="outlined"
                    />
                    {renderStreamingLinks(song)}
                  </Box>
                }
              />
              {selectedSong?._id === song._id && (
                <ListItemSecondaryAction>
                  <Autocomplete
                    value={selectedKey}
                    onChange={(event, newValue) => setSelectedKey(newValue)}
                    options={MUSICAL_KEYS}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Key"
                        sx={{ width: 100 }}
                      />
                    )}
                    sx={{ minWidth: 100 }}
                  />
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!selectedSong || !selectedKey}
        >
          Add Song
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SongSelector;
